import { ASTNode } from 'graphql'
import { print } from 'graphql/language/printer'
import { ContentTypes } from '../enums'
import { KeyVal } from '../types'

interface IOptions {
  headers: KeyVal<string>
  method: string
  body?: FormData | string
}

export interface IFiles {
  key: string
  files: File[]
}

export class HTTPService {
  private headers: KeyVal<string>

  constructor(headers: KeyVal<string> = {}) {
    this.headers = {
      accept: ContentTypes.JSON,
      'content-type': ContentTypes.JSON,
      ...headers
    }
  }

  protected get<T>(uri: string): Promise<T> {
    return this.buildRequest('GET', uri)
  }

  protected post<T>(uri: string, body: KeyVal<any>, fileObject?: IFiles): Promise<T> {
    return this.buildRequest('POST', uri, body, fileObject)
  }

  protected patch<T>(uri: string, body: KeyVal<any>, fileObject?: IFiles): Promise<T> {
    return this.buildRequest('PATCH', uri, body, fileObject)
  }

  protected put<T>(uri: string, body: KeyVal<any>, fileObject?: IFiles): Promise<T> {
    return this.buildRequest('PUT', uri, body, fileObject)
  }

  protected delete<T>(uri: string, body?: KeyVal<any>): Promise<T> {
    return this.buildRequest('DELETE', uri, body)
  }

  protected graphql<T>(uri: string, variables: object, ast: ASTNode): Promise<T> {
    const query = print(ast)
    return this.post(uri, { query, variables })
  }

  private buildRequest(method: string, uri: string, body?: KeyVal<any>, fileObject?: IFiles): Promise<any> {
    return new Promise((resolve, reject): void => {
      fetch(uri, this.getOptions(method, body, fileObject))
        .then((res: Response) => this.onResponse(res, resolve, reject))
        .catch((err: Error) => reject(err))
    })
  }

  private getOptions(method: string, body?: KeyVal<any>, fileObject?: IFiles): IOptions {
    const options: IOptions = {
      headers: this.headers,
      method
    }

    // If request has files, use FormData; else, use json
    if (fileObject && fileObject.files.length > 0) {
      const formData = new FormData()
      fileObject.files.forEach(file => formData.append(`${fileObject.key}`, file))
      if (body) {
        for (const key in body) {
          formData.append(key, body[key])
        }
      }
      options.body = formData
    } else if (body) {
      switch (options.headers['content-type']) {
        case ContentTypes.JSON:
        default:
          options.body = JSON.stringify(body)
          break
      }
    }

    return options
  }

  private async onResponse(res: Response, resolve: Function, reject: Function): Promise<any> {
    // Parse and return json body or plain response
    const contentType = res.headers.get('content-type')
    const jsonResponse = contentType && contentType.indexOf(ContentTypes.JSON) !== -1

    if (jsonResponse) {
      try {
        const obj = await res.json()
        return res.ok
          ? resolve(obj)
          : reject(obj)
      } catch (err) { }
    }

    return res.ok
      ? resolve(res)
      : reject(reject)
  }
}
